import React from 'react';

import { Box, Typography, List, CircularProgress } from '@mui/material';

import { ReactComponent as NoResultsIcon } from '../../assets/icons/no_results_icon.svg';
import { SaveSheet, SheetStatus } from '../../types/SaveSheet';
import { SelectorType } from '../../types/SelectorType';
import { CurrentUser, User } from '../../types/User';

import FlaggedCodesContainer from './components/FlaggedCodesContainer';
import SavedCodesContainer from './components/SavedCodesContainer';
import SavedSheetsContainer from './components/SavedSheetsContainer';
import Selector from './components/Selector/Selector';
import './UserCodesContainer.scss';

type Props = {
  user: CurrentUser | User;
  type: 'admin' | 'florence';
  height?: number | string;
  width?: number;
  style?: React.CSSProperties;
};

const getPreservedTabState = (appType: 'admin' | 'florence') => {
  if (appType === 'admin') return 'OPNotes';

  const lastTab = localStorage.getItem('lastHomepageTabSelected');
  if (!lastTab) return 'OPNotes';

  return lastTab as SelectorType;
};

const UserCodesContainer: React.FC<Props> = ({
  user,
  type,
  style,
  height,
  width,
}) => {
  const [openedTab, setOpenedTab] = React.useState<SelectorType>(
    getPreservedTabState(type)
  );
  const [selectedSheet, setSelectedSheet] = React.useState<SaveSheet | null>(
    null
  );
  const [deleteContainerOpen, setDeleteContainerOpen] = React.useState(false);
  const [renameContainerOpen, setRenameContainerOpen] = React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState<SheetStatus>();
  const [searchTerms, setSearchTerms] = React.useState<string>('');

  const handleCodeChange = (state: SelectorType) => {
    if (type === 'florence') {
      localStorage.setItem('lastHomepageTabSelected', state);
    }
    setOpenedTab(state);
  };

  const renderNoSubscriptionText = (type: SelectorType) => {
    let noRecordsText = '';
    switch (type) {
      case 'Codes':
        noRecordsText =
          'In order to view your Saved Codes you need an Active Subscription.';
        break;
      case 'Flagged':
        noRecordsText = 'No flagged codes';
        break;
      case 'OPNotes':
        noRecordsText =
          'In order to view your Op Notes you need an Active Subscription.';
        break;
      case 'ClinicNotes':
        noRecordsText =
          'In order to view your Clinic Notes you need an Active Subscription.';
        break;
    }

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          mt: '50px',
        }}
      >
        <Typography color='text.secondary' sx={{ mt: '18px' }}>
          {noRecordsText}
        </Typography>
      </Box>
    );
  };

  const renderNoRecordsText = (type: SelectorType) => {
    let noRecordsText = '';
    switch (type) {
      case 'Codes':
        noRecordsText = 'No Favorite Codes yet';
        break;
      case 'Flagged':
        noRecordsText = 'No Flagged codes';
        break;
      case 'OPNotes':
        noRecordsText = 'No OP Notes found';
        break;
      case 'ClinicNotes':
        noRecordsText = 'No Clinic Notes Found';
        break;
    }
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          mt: '50px',
        }}
      >
        <NoResultsIcon />
        <Typography color='text.secondary' sx={{ mt: '18px' }}>
          {noRecordsText}
        </Typography>
      </Box>
    );
  };

  const renderLoadingSpinner = () => (
    <Box style={{ display: 'flex', justifyContent: 'center', paddingTop: 24 }}>
      <CircularProgress color='primary' size='32px' />
    </Box>
  );

  const openDeleteSheetContainer = (savedSheet: SaveSheet) => {
    setDeleteContainerOpen(true);
    setSelectedSheet(savedSheet);
  };

  const openRenameSheetContainer = (savedSheet: SaveSheet) => {
    setRenameContainerOpen(true);
    setSelectedSheet(savedSheet);
  };

  return (
    <Box
      style={{
        ...style,
        width: width ?? '100%',
        paddingBottom: '24px',
        marginBottom: '16px',
      }}
      sx={
        type === 'admin'
          ? {
              height: height ?? 0,
              minHeight: '500px',
              overflow: 'hidden',
              padding: 0,
            }
          : {}
      }
      className='user-content-container'
    >
      <Box
        sx={
          type === 'admin'
            ? {
                justifyContent: 'space-between',
                margin: '20px',
                marginBottom: '0px',
                flexWrap: 'wrap',
              }
            : { justifyContent: 'space-between', flexWrap: 'wrap' }
        }
        className='row'
      >
        <Selector
          type={type}
          handleChange={handleCodeChange}
          value={openedTab}
        />
      </Box>
      <List
        sx={
          type === 'admin'
            ? {
                overflowY: 'visible',
                padding: '20px',
              }
            : {}
        }
      >
        {openedTab === 'OPNotes' && (
          <SavedSheetsContainer
            type={type}
            user={user}
            clinicNotes={false}
            statusFilter={statusFilter}
            searchTerms={searchTerms}
            renderLoadingSpinner={renderLoadingSpinner}
            renderNoRecordsText={renderNoRecordsText}
            renderNoSubscriptionText={renderNoSubscriptionText}
            openDeleteSheetContainer={openDeleteSheetContainer}
            openRenameSheetContainer={openRenameSheetContainer}
          />
        )}
        {openedTab === 'ClinicNotes' && (
          <SavedSheetsContainer
            type={type}
            user={user}
            clinicNotes={true}
            statusFilter={statusFilter}
            searchTerms={searchTerms}
            renderLoadingSpinner={renderLoadingSpinner}
            renderNoRecordsText={renderNoRecordsText}
            renderNoSubscriptionText={renderNoSubscriptionText}
            openDeleteSheetContainer={openDeleteSheetContainer}
            openRenameSheetContainer={openRenameSheetContainer}
          />
        )}
        {openedTab === 'Codes' && (
          <SavedCodesContainer
            type={type}
            user={user}
            renderLoadingSpinner={renderLoadingSpinner}
            renderNoRecordsText={renderNoRecordsText}
            renderNoSubscriptionText={renderNoSubscriptionText}
          />
        )}
        {openedTab === 'Flagged' && (
          <FlaggedCodesContainer
            type={type}
            user={user}
            renderLoadingSpinner={renderLoadingSpinner}
            renderNoRecordsText={renderNoRecordsText}
          />
        )}
      </List>
    </Box>
  );
};

export default UserCodesContainer;
